import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DataTableV2, Field, Modal, SearchField } from "components/commons";
import useDataTable from "hooks/useDataTable";
import { useModal } from "hooks";
import { initialFilterState } from "modules/shift-records/shift-records-filter.state";
import locale from "localization";
import { FormMode } from "enums";
import { formatPumpNumber } from "utils/text.utils";
import { cashTransactionDetailColumn } from "../shift-records-details-cash-non-cash-column";
import { cashTransactionDetailMapper } from "../shift-records-details-cash-non-cash.mapper";
import AdjustPaymentMethodDrawer from "../shift-details-non-cash-summary/payment-method-transaction-modal/adjust-payment-method-drawer";
import styles from "./cash-transaction-modal.module.scss";

export default function CashTransactionModal({
  cashRow,
  cashTransactions = [],
  allCashTransactions = [],
  transactionsItemDetails = [],
  pageMode,
  updateTransactionPaymentMethod,
  ...modal
}) {
  const [pageState, setPageState] = useState({ page: 1, perPage: 10 });
  const [searchKey, setSearchKey] = useState("");

  const isView = pageMode === FormMode.View;

  const adjustPaymentMethodDrawer = useModal();
  const [adjustedTransaction, setAdjustedTransaction] = useState(null);
  const [adjustedRowIndex, setAdjustedRowIndex] = useState(null);

  const onAdjustPaymentMethod = useCallback(
    (transaction, rowIndex) => {
      setAdjustedTransaction(transaction);
      setAdjustedRowIndex(rowIndex);
      adjustPaymentMethodDrawer.show({});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const title = useMemo(() => {
    if (!cashRow) {
      return locale.cash;
    }
    const pump =
      cashRow.type === "fuel" && cashRow.pumpNumber
        ? ` ${formatPumpNumber(String(cashRow.pumpNumber).split(",")[0])}`
        : "";
    return `${locale.cash} - ${cashRow.name ?? ""}${pump}`;
  }, [cashRow]);

  const filteredTransactions = useMemo(() => {
    if (!searchKey) {
      return cashTransactions;
    }
    const value = searchKey.toLowerCase();
    return cashTransactions.filter((t) => t.transactionNumber?.toLowerCase().includes(value));
  }, [cashTransactions, searchKey]);

  const paginatedTransactions = useMemo(() => {
    const { page, perPage } = pageState;
    const start = (page - 1) * perPage;
    return filteredTransactions.slice(start, start + perPage);
  }, [filteredTransactions, pageState]);

  const columns = useMemo(
    () =>
      isView
        ? cashTransactionDetailColumn.filter((column) => column.key !== "actions")
        : cashTransactionDetailColumn,
    [isView],
  );

  const { table } = useDataTable({
    value: paginatedTransactions,
    filter: { initialState: initialFilterState(), mapper: (data) => data },
    table: {
      key: "cashDetail",
      name: "cashTransactions",
      mapper: (val, index) => ({
        ...cashTransactionDetailMapper({
          val,
          onAdjustPaymentMethod: isView
            ? undefined
            : (transaction) => onAdjustPaymentMethod(transaction, index),
        }),
        isSelected: adjustPaymentMethodDrawer.active && index === adjustedRowIndex,
      }),
      columns,
    },
  });

  useEffect(() => {
    setPageState(table.pageState);
  }, [table.pageState]);

  useEffect(() => {
    setSearchKey("");
    setAdjustedTransaction(null);
    setAdjustedRowIndex(null);
    adjustPaymentMethodDrawer.close();
    table.applyPage({ page: 1, perPage: 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.active]);

  return (
    <Modal
      {...modal}
      title={title}
      titleBlack
      headerExtra={
        <Field className={styles.search}>
          <SearchField
            placeholder={locale.transactionNo}
            value={searchKey}
            searchWithSymbols={false}
            restrict={false}
            onChange={(_, { value }) => setSearchKey(value)}
            onEnter={() => {}}
            onClear={() => setSearchKey("")}
          />
        </Field>
      }
      textHeaderStyle={styles.textHeader}
      headerStyle={styles.header}
      className={styles.modalBox}
    >
      <DataTableV2
        {...table}
        className={styles.tableContainer}
        result={{ cashDetail: paginatedTransactions }}
        totalCount={filteredTransactions.length}
        refetch={() => {}}
        hideNoSearchResultRender
        tableVersion="v2"
        styleV2={true}
        size="small"
        childBottom={
          <Button onClick={() => modal?.close?.()} style={{ marginTop: "1.5em" }} outline>
            {locale.back}
          </Button>
        }
      />
      <AdjustPaymentMethodDrawer
        {...adjustPaymentMethodDrawer}
        transaction={adjustedTransaction}
        shiftId={transactionsItemDetails[0]?.shiftId}
        transactionsItemDetails={transactionsItemDetails}
        allPaymentMethodRows={allCashTransactions}
        onUpdatePaymentMethod={updateTransactionPaymentMethod}
      />
    </Modal>
  );
}
